/**
 * Contract Manager — List, create, and manage FDR delegation agreements.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ContractWorkspace } from './ContractTabs';
import ScopeCompareModal from './ScopeCompareModal';
import FdrMatrixModal from './FdrMatrixModal';

const API = process.env.REACT_APP_API_URL;

const STATUS_CFG = {
  draft:             { label: 'Draft',       color: 'bg-slate-100 text-slate-600' },
  pending_execution: { label: 'Pending',     color: 'bg-amber-50 text-amber-700' },
  active:            { label: 'Active',      color: 'bg-emerald-50 text-emerald-700' },
  expired:           { label: 'Expired',     color: 'bg-slate-100 text-slate-500' },
  terminated:        { label: 'Terminated',  color: 'bg-red-50 text-red-600' },
  suspended:         { label: 'Suspended',   color: 'bg-orange-50 text-orange-600' },
};

// Dot color per status for the popover menu — the pill colors above are
// washes; the menu needs a solid marker the eye can catch.
const STATUS_DOT = {
  draft: 'bg-slate-400', pending_execution: 'bg-amber-500', active: 'bg-emerald-500',
  expired: 'bg-slate-300', terminated: 'bg-red-500', suspended: 'bg-orange-500',
};

/**
 * Asana-style status control: the pill itself is the button; clicking it
 * opens a small menu of colored options with a check on the current one.
 * Changing status is a human decision — this just puts it where the eye
 * already is.
 */
export function StatusPill({ contract, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const current = STATUS_CFG[contract.status] || STATUS_CFG.draft;
  const pick = async (status) => {
    if (busy || status === contract.status) { setOpen(false); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/fdr/contracts/${contract.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not update the status');
      setOpen(false);
      onChanged?.();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };
  return (
    <div ref={ref} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen((v) => !v)} disabled={busy}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition hover:ring-2 hover:ring-slate-200 ${current.color}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[contract.status] || STATUS_DOT.draft}`} />
        {busy ? 'Saving…' : current.label}
        <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" className="opacity-50"><path d="M5.5 7.5L10 12l4.5-4.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      {open && (
        <div className="absolute z-30 mt-1.5 left-0 w-44 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden py-1">
          {Object.entries(STATUS_CFG).map(([k, v]) => (
            <button key={k} type="button" onClick={() => pick(k)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left hover:bg-slate-50 ${k === contract.status ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[k]}`} />
              <span className="flex-1">{v.label}</span>
              {k === contract.status && (
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-emerald-600"><path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round"/></svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const PROVISION_LABELS = [
  { key: 'has_compliance_program_req', label: 'Compliance Program' },
  { key: 'has_code_of_conduct_req', label: 'Code of Conduct' },
  { key: 'has_compliance_officer_req', label: 'Compliance Officer' },
  { key: 'has_training_req', label: 'Training' },
  { key: 'has_monitoring_req', label: 'Monitoring/Auditing' },
  { key: 'has_reporting_req', label: 'Reporting Mechanism' },
  { key: 'has_enforcement_req', label: 'Enforcement/Discipline' },
  { key: 'has_downstream_oversight_req', label: 'Downstream Oversight' },
  { key: 'has_exclusion_screening_req', label: 'Exclusion Screening' },
  { key: 'has_records_access_req', label: 'Records Access (CMS/OIG)' },
  { key: 'has_data_privacy_req', label: 'Data Privacy/PHI' },
  { key: 'has_hipaa_baa', label: 'HIPAA BAA' },
  { key: 'has_performance_standards', label: 'Performance Standards' },
  { key: 'has_insurance_req', label: 'Insurance Requirements' },
];

export default function ContractManager({ onOpenEntity }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [entities, setEntities] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showScope, setShowScope] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [contractDetail, setContractDetail] = useState(null);
  const [functionForContract, setFunctionForContract] = useState(null);

  const [form, setForm] = useState({
    entity_id: '', contract_number: '', title: '', description: '',
    effective_date: '', expiration_date: '', auto_renew: false,
    termination_notice_days: 90, status: 'draft',
  });
  const [provisions, setProvisions] = useState(
    PROVISION_LABELS.reduce((acc, p) => ({ ...acc, [p.key]: false }), {})
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${API}/api/fdr/contracts?status=${statusFilter}`
        : `${API}/api/fdr/contracts`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setContracts(data.contracts || []);
    } catch {}
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        // All non-terminated entities — the old ?status=active filter silently hid
        // pending_review entities from the picker with no explanation.
        const res = await fetch(`${API}/api/fdr/entities`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setEntities((data.entities || []).filter(e => !['terminated', 'inactive'].includes(e.status)));
      } catch {}
    })();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.entity_id || !form.contract_number || !form.title || !form.effective_date || !form.expiration_date) {
      setError('Entity, contract number, title, and dates are required'); return;
    }
    setCreating(true); setError('');
    try {
      const res = await fetch(`${API}/api/fdr/contracts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, ...provisions }),
      });
      const data = await res.json();
      if (data.success) { setShowCreate(false); load(); }
      else setError(data.message);
    } catch { setError('Failed to create contract'); }
    setCreating(false);
  };

  const handleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); setContractDetail(null); return; }
    setExpandedId(id);
    try {
      const res = await fetch(`${API}/api/fdr/contracts/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setContractDetail(data);
    } catch {}
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Delegation Agreements</h2>
          <p className="text-sm text-slate-500 mt-0.5">{contracts.length} contract{contracts.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
            <option value="">All statuses</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={() => setShowScope(true)}
            className="px-4 py-2.5 bg-white border border-emerald-300 text-emerald-700 rounded-xl text-sm font-semibold hover:bg-emerald-50 transition-all"
            title="What every contract says is in or out of scope, side by side: functions, services, populations, codes. Quotes only.">
            Scope across vendors
          </button>
          <button onClick={() => setShowMatrix(true)}
            className="px-4 py-2.5 bg-white border border-orange-300 text-orange-700 rounded-xl text-sm font-semibold hover:bg-orange-50 transition-all"
            title="The Master FDR Matrix on screen: one row per vendor, delegated functions, contract dates, accreditations, contacts — filterable, with per-vendor questions answered from the register record.">
            FDR Matrix
          </button>
          <a href={`${API}/api/fdr/contracts/matrix.xlsx`}
            className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-all"
            title="The Master FDR Matrix as an Excel workbook, generated from the live register: one row per vendor, delegated functions as columns, contract dates, accreditations, and FDR contacts.">
            Export FDR Matrix
          </a>
          <button onClick={() => setShowCreate(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all">
            Add Contract
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading...</div>
        ) : contracts.length === 0 ? (
          <div className="p-12 text-center text-slate-400">No contracts found</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left px-6 py-3 font-semibold text-slate-500">Contract</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Entity</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Effective</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Expires</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Functions</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500">Provisions</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map(c => {
                const s = STATUS_CFG[c.status] || STATUS_CFG.draft;
                const daysLeft = daysUntil(c.expiration_date);
                const provCount = PROVISION_LABELS.filter(p => c[p.key]).length;
                const isExpanded = expandedId === c.id;

                return (
                  <React.Fragment key={c.id}>
                    <tr onClick={() => handleExpand(c.id)}
                      className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-slate-800">{c.contract_number}</div>
                        <div className="text-slate-500 text-xs mt-0.5 max-w-[200px] truncate">{c.title}</div>
                      </td>
                      <td className="px-4 py-4">
                        <button onClick={(e) => { e.stopPropagation(); onOpenEntity(c.entity_id); }}
                          className="text-orange-600 hover:text-orange-700 font-medium hover:underline">
                          {c.entity_name}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <StatusPill contract={c} onChanged={load} />
                      </td>
                      <td className="px-4 py-4 text-slate-600">{c.effective_date?.split('T')[0]}</td>
                      <td className="px-4 py-4">
                        <span className={`text-sm ${daysLeft !== null && daysLeft < 30 ? 'text-red-600 font-semibold' : daysLeft !== null && daysLeft < 90 ? 'text-amber-600' : 'text-slate-600'}`}>
                          {c.expiration_date?.split('T')[0]}
                        </span>
                        {daysLeft !== null && daysLeft < 90 && (
                          <div className="text-xs text-slate-400 mt-0.5">{daysLeft <= 0 ? 'Expired' : `${daysLeft}d left`}</div>
                        )}
                        {c.terminated_at && (
                          <div className="text-xs font-semibold text-red-600 mt-0.5">{c.status === 'terminated' ? 'Terminated' : 'Ends'} {String(c.terminated_at).slice(0, 10)}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-slate-600">{c.function_count || 0}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${provCount >= 12 ? 'bg-emerald-500' : provCount >= 8 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${(provCount / 14) * 100}%` }} />
                          </div>
                          <span className="text-xs text-slate-500">{provCount}/14</span>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && contractDetail && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-slate-50/50">
                          <ContractWorkspace detail={contractDetail} onAddFunction={() => setFunctionForContract(c)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ScopeCompareModal open={showScope} onClose={() => setShowScope(false)} />
      <FdrMatrixModal open={showMatrix} onClose={() => setShowMatrix(false)} />

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">New Delegation Agreement</h3>
            </div>
            <form onSubmit={handleCreate} className="px-8 py-6 space-y-4">
              {error && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{error}</div>}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">FDR Entity *</label>
                  <select value={form.entity_id} onChange={e => setForm({...form, entity_id: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" required>
                    <option value="">Select entity...</option>
                    {entities.map(e => <option key={e.id} value={e.id}>{e.name}{e.status !== 'active' ? ` (${e.status.replace(/_/g, ' ')})` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contract Number *</label>
                  <input value={form.contract_number} onChange={e => setForm({...form, contract_number: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Effective Date *</label>
                  <input type="date" value={form.effective_date} onChange={e => setForm({...form, effective_date: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Expiration Date *</label>
                  <input type="date" value={form.expiration_date} onChange={e => setForm({...form, expiration_date: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">CMS Chapter 21 Required Provisions</label>
                <div className="grid grid-cols-2 gap-2 bg-slate-50 rounded-xl p-4">
                  {PROVISION_LABELS.map(p => (
                    <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={provisions[p.key]}
                        onChange={e => setProvisions({...provisions, [p.key]: e.target.checked})}
                        className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/20" />
                      <span className="text-slate-700">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowCreate(false)}
                  className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={creating}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Contract'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {functionForContract && (
        <AddFunctionModal contract={functionForContract}
          onClose={() => setFunctionForContract(null)}
          onCreated={async () => {
            setFunctionForContract(null);
            // reload the expanded detail
            try {
              const res = await fetch(`${API}/api/fdr/contracts/${expandedId}`, { credentials: 'include' });
              const data = await res.json();
              if (data.success) setContractDetail(data);
            } catch {}
            load();
          }} />
      )}
    </div>
  );
}

// CMS audit-protocol-aligned delegated function categories (migration 072).
const FUNCTION_CATEGORIES = [
  'claims_processing', 'claims_adjudication', 'appeals_grievances', 'utilization_management',
  'care_management', 'credentialing', 'network_management', 'pharmacy_benefits',
  'enrollment_disenrollment', 'marketing_communications', 'member_services', 'quality_improvement',
  'data_analytics', 'billing_coding', 'provider_education', 'formulary_management',
  'prior_authorization', 'other',
];

/** Add Delegated Function — wires the previously-orphaned POST /contracts/:id/functions. */
/**
 * The one contract workspace — provisions checklist, delegated functions,
 * and the agent review. Rendered identically by the platform Contracts
 * tab and the vendor page, so a contract looks the same through every door.
 */
// The contract workspace lives in ContractTabs.jsx (Overview, Scope, Compliance
// risks, Delegated functions, Service levels, Documents); re-exported here so
// the vendor screen keeps importing it from this file.
export { ContractWorkspace };

export function AddFunctionModal({ contract, onClose, onCreated }) {
  const [form, setForm] = useState({
    function_category: 'claims_processing', function_description: '',
    performance_standards: '', reporting_requirements: '',
    monitoring_frequency: 'quarterly', monitoring_method: 'desk_review',
    sub_delegation_allowed: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const cls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const res = await fetch(`${API}/api/fdr/contracts/${contract.id}/functions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) onCreated(); else setErr(data.message || 'Failed');
    } catch { setErr('Failed to add function'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800">Add Delegated Function</h3>
          <p className="text-xs text-slate-400 mt-0.5">{contract.title || contract.contract_number} — drives the monitoring calendar and FTEAM work plan</p>
        </div>
        <form onSubmit={submit} className="px-8 py-5 space-y-4">
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{err}</div>}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Function *</label>
            <select value={form.function_category} onChange={e => setForm({ ...form, function_category: e.target.value })} className={cls}>
              {FUNCTION_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">What exactly is delegated *</label>
            <textarea value={form.function_description} onChange={e => setForm({ ...form, function_description: e.target.value })} required rows={2}
              placeholder="Scope of the delegated work, in plain language" className={cls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Performance standards / SLAs</label>
            <textarea value={form.performance_standards} onChange={e => setForm({ ...form, performance_standards: e.target.value })} rows={2}
              placeholder="e.g., 95% clean claims adjudicated within 30 days; UM standard decisions within 14 days" className={cls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reporting requirements</label>
            <input value={form.reporting_requirements} onChange={e => setForm({ ...form, reporting_requirements: e.target.value })}
              placeholder="What reports, how often, to whom" className={cls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Monitoring frequency</label>
              <select value={form.monitoring_frequency} onChange={e => setForm({ ...form, monitoring_frequency: e.target.value })} className={cls}>
                <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
                <option value="semi_annually">Semi-annually</option><option value="annually">Annually</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Monitoring method</label>
              <select value={form.monitoring_method} onChange={e => setForm({ ...form, monitoring_method: e.target.value })} className={cls}>
                <option value="desk_review">Desk review</option><option value="site_visit">Site visit</option>
                <option value="hybrid">Hybrid</option><option value="sampling">Sampling</option><option value="automated">Automated</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.sub_delegation_allowed}
              onChange={e => setForm({ ...form, sub_delegation_allowed: e.target.checked })}
              className="rounded border-slate-300 text-orange-600 focus:ring-orange-500/30" />
            Sub-delegation permitted (downstream entities must then be registered)
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-50">
              {saving ? 'Adding…' : 'Add Function'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
